import './styles.scss';

import Button from '@/common/Button';
import { ButtonTypes } from '@/common/Button/types';
import Form from '@/common/Form';
import { ReactElement } from 'react';
import Textarea from '@/common/Textarea';
import { TrainingChecklistTableTypes } from '@/pages/Users/components/TrainingChecklistTable/types';
import { getClasses } from '@/utils/strings';

export type EvaluationQuestionFieldProps = {
  question: TrainingChecklistTableTypes.EvaluationQuestionSnapshot;
  options: TrainingChecklistTableTypes.EvaluationAnswerOptionSnapshot[];
  readOnly: boolean;
  index: number;
};

// Colors and toggle behavior come from the option snapshot (option.color, option.showsNotes) rather than
// being hardcoded here, so this component works for both the 3-option and Yes/No answer types unchanged.
const EvaluationQuestionField = ({ question, options, readOnly, index }: EvaluationQuestionFieldProps): ReactElement => {
  const optionFieldName = `answers.${question.id}.optionId`;
  const noteFieldName = `answers.${question.id}.note`;
  const sortedOptions = [...options].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="EvaluationQuestionField" id={question.id}>
      <p className="EvaluationQuestionField-Text">
        {index + 1}. {question.questionText}
      </p>
      <Form.Watch name={optionFieldName}>
        {([selectedOptionId]: string[], { setValue, trigger }): ReactElement => (
          <div className="EvaluationQuestionField-Options">
            {sortedOptions.map((option) => {
              const checked = selectedOptionId === option.id;
              return (
                <RadioButton
                  key={option.id}
                  checked={checked}
                  disabled={readOnly}
                  onClick={
                    readOnly
                      ? undefined
                      : (): void => {
                          setValue(optionFieldName, checked ? undefined : option.id, { shouldDirty: true });
                          trigger();
                        }
                  }
                  style={{
                    color: checked ? '#fff' : option.color,
                    borderColor: option.color,
                    backgroundColor: checked ? option.color : undefined,
                  }}
                >
                  {option.displayLabel}
                </RadioButton>
              );
            })}
            <Form.Input name={optionFieldName} style={{ display: 'none' }} />
          </div>
        )}
      </Form.Watch>
      <Form.Watch name={optionFieldName}>
        {([selectedOptionId]: string[]): ReactElement | null => {
          const selectedOption = sortedOptions.find((option) => option.id === selectedOptionId);
          if (!selectedOption?.showsNotes) return null;

          return (
            <Form.Field name={noteFieldName} label={readOnly ? 'NOTES:' : 'NOTES: *'} className="EvaluationQuestionField-Notes">
              <Form.Input
                name={noteFieldName}
                as={Textarea}
                placeholder={question.answerTypeNotesPlaceholder}
                disabled={readOnly}
                validate={!readOnly}
              />
            </Form.Field>
          );
        }}
      </Form.Watch>
    </div>
  );
};

// TODO: replace with common component when created
const RadioButton = ({ checked, onClick, ...props }: ButtonTypes.Props & { checked?: boolean }) => {
  return (
    <Button
      variant="light"
      {...props}
      onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      className={getClasses('RadioButton', checked ? 'RadioButton-Checked' : undefined, props?.className)}
    />
  );
};

export default EvaluationQuestionField;
